// client/src/pages/admin/AdminLayout.jsx

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
// Import your logo file:
import Logo from '../../assets/Venzowood.webp';

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [adminName, setAdminName] = useState('Admin');
  const [slidersDropdownOpen, setSlidersDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, read admin name from localStorage (if stored)
  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (adminData) {
      const parsedData = JSON.parse(adminData);
      setAdminName(parsedData.name || 'Admin');
    }
  }, []);

  // Confirm & clear token/localStorage on logout
  const handleLogout = () => {
    Swal.fire({
      title: 'Logout Confirmation',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#000',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, logout'
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('token');
        localStorage.removeItem('adminData');
        navigate('/admin/login');
      }
    });
  };

  // Returns Tailwind classes for “active” link vs. normal
  const isActive = (path) => {
    return location.pathname === path
      ? 'bg-gray-800 text-white'
      : 'text-gray-300 hover:bg-gray-700 hover:text-white';
  };

  // Check if any slider route is active
  const isSliderActive = () => {
    return location.pathname === '/admin/slider' || location.pathname === '/admin/hero-slider';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ─── SIDEBAR ───────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#161717] transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0`}
      >
        {/* Logo Container (height h-20, background #161717) */}
        <div className="flex items-center justify-left h-20 bg-[#161717] pl-[15px] pt-[15px]">
          {/* The logo is scaled to height=20 (80px), width=auto */}
          <img src={Logo} alt="Venzowood Admin" className="h-20 w-auto" />
        </div>

        {/* ─── NAVIGATION LINKS ───────────────────────────────────────────────── */}
        <div className="px-2 py-4">
          <nav className="space-y-1">
            {/* Dashboard */}
            <Link
              to="/admin"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Dashboard
            </Link>

            {/* Analytics */}
            <Link
              to="/admin/analytics"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/analytics'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Analytics
            </Link>

            {/* Categories */}
            <Link
              to="/admin/categories"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/categories'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              Categories
            </Link>

            {/* Products */}
            <Link
              to="/admin/products"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/products'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              Products
            </Link>

            {/* Orders */}
            <Link
              to="/admin/orders"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/orders'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              Orders
            </Link>

            {/* Quotations */}
            <Link
              to="/admin/quotations"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/quotations'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Quotations
            </Link>

            {/* Freeze History */}
            <Link
              to="/admin/freeze-history"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/freeze-history'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Freeze History
            </Link>

            {/* Users (User Icon) */}
            <Link
              to="/admin/users"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/users'
              )}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6M4 20h5v-2a4 4 0 013-3.87M4 20a4 4 0 00-3-3.87V4a4 4 0 014-4h12a4 4 0 014 4v12.13A4 4 0 0020 20"
                />
              </svg>
              Users
            </Link>

            {/* Story Updates (Camera Icon) */}
            <Link
              to="/admin/story-updates"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/story-updates'
              )}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7h18M7 7l3-3m0 0l3 3M14 7h2a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2m2 0v10m4-10v10m-4-6h4"
                />
              </svg>
              Story Updates
            </Link>

            {/* E-Catalogue */}
            <Link
              to="/admin/e-catalogue"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/e-catalogue'
              )}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              E-Catalogue
            </Link>

            {/* Sliders Dropdown */}
            <div className="space-y-1">
              <button
                onClick={() => setSlidersDropdownOpen(!slidersDropdownOpen)}
                className={`group flex items-center justify-between w-full px-2 py-2 text-base font-medium rounded-md ${isSliderActive()
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
              >
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-3 h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Sliders
                </div>
                <svg
                  className={`ml-3 h-5 w-5 transform transition-transform duration-200 ${slidersDropdownOpen ? 'rotate-180' : ''
                    }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Items */}
              {slidersDropdownOpen && (
                <div className="ml-6 space-y-1">
                  <Link
                    to="/admin/slider"
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname === '/admin/slider'
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-3 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Slider Images
                  </Link>

                  <Link
                    to="/admin/hero-slider"
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname === '/admin/hero-slider'
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-3 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    Hero Slider
                  </Link>
                </div>
              )}
            </div>

            {/* Events Management */}
            <Link
              to="/admin/events"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/events'
              )}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Events
            </Link>

            {/* Event Registrations */}
            <Link
              to="/admin/event-registrations"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/event-registrations'
              )}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-3 h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Event Registrations
            </Link>



            {/* Settings */}
            <Link
              to="/admin/settings"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/settings'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Settings
            </Link>

            {/* Profile */}
            <Link
              to="/admin/profile"
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${isActive(
                '/admin/profile'
              )}`}
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Profile
            </Link>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              <svg
                className="mr-3 h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout
            </button>
          </nav>
        </div>
      </div>

      {/* ─── MAIN CONTENT ───────────────────────────────────────────────────────── */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top Navigation Bar (height h-20) */}
        <div className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex justify-between h-20 px-4 md:px-8">
            <div className="flex items-center">
              {/* Sidebar toggle (mobile only) */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-900 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center">
              <span className="hidden md:block text-sm font-medium text-gray-700 mr-2">
                Welcome, {adminName}
              </span>
              <Link
                to="/admin/profile"
                className="max-w-xs bg-gray-800 rounded-full flex items-center text-sm focus:outline-none"
              >
                <span className="sr-only">Open user menu</span>
                <div className="h-8 w-8 rounded-full bg-gray-500 flex items-center justify-center text-white">
                  {adminName.charAt(0).toUpperCase()}
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Page Content Outlet */}
        <main className="flex-1 overflow-y-auto bg-gray-100">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
